import React from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { UnitsProvider } from "@/contexts/UnitsContext";
import { TimezoneProvider } from "@/contexts/TimezoneContext";
import { Toaster } from "@/components/ui/sonner";
import { useIsMobile } from "@/hooks/useIsMobile";
import { sanitizeHome } from "@/lib/navigation";
import Layout from "@/components/layout/Layout";
import LoginPage from "@/pages/LoginPage";
import SignUpPage from "@/pages/SignUpPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import { CHANGE_PASSWORD_PATH, passwordChangeRedirect } from "@/lib/passwordChange";
import DashboardPage from "@/pages/DashboardPage";
import EquipmentsPage from "@/pages/EquipmentsPage";
import EquipmentTypesPage from "@/pages/EquipmentTypesPage";
import EquipmentGroupsPage from "@/pages/EquipmentGroupsPage";
import UsersPage from "@/pages/UsersPage";
import SettingsPage from "@/pages/SettingsPage";
import AccessControlPage from "@/pages/AccessControlPage";
import AIHelperPage from "@/pages/AIHelperPage";
import SourceRegistryPage from "@/pages/SourceRegistryPage";
import WidgetLibraryPage from "@/pages/WidgetLibraryPage";
import EngineModelsPage from "@/pages/EngineModelsPage";
import IoTSignupPage from "@/pages/IoTSignupPage";
import GeofencesPage from "@/pages/GeofencesPage";
import RoutesPage from "@/pages/RoutesPage";
import AlertsPage from "@/pages/AlertsPage";
import EquipmentMonitorPage from "@/pages/EquipmentMonitorPage";
import EquipmentAlertsPage from "@/pages/EquipmentAlertsPage";
import EquipmentMonitorPrint from "@/pages/EquipmentMonitorPrint";
import FuelAuditPage from "@/pages/FuelAuditPage";
import FuelControlPage from "@/pages/FuelControlPage";
import TripsPage from "@/pages/TripsPage";
import TripDetailPage from "@/pages/TripDetailPage";
import TripReportPrint from "@/pages/TripReportPrint";
import ReportsPage from "@/pages/ReportsPage";
import OperationsActivityPage from "@/pages/OperationsActivityPage";
import OperatorDashboardPage from "@/pages/OperatorDashboardPage";
// ========================================================================
// DESENVOLVIMENTO: Login desabilitado temporariamente
// Será reativado após conclusão da FASE 4
// ========================================================================
const DEV_MODE_NO_AUTH = false;

const LoadingScreen = () => (
  <div className="flex min-h-[100dvh] items-center justify-center bg-background">
    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const getOperatorDashboardPath = (visibleEquipments) => {
  if (visibleEquipments?.length === 1) {
    return `/equipments/${visibleEquipments[0].id}/operator-dashboard`;
  }
  return "/";
};

// Protected Route wrapper - bypassed in dev mode
const ProtectedRoute = ({ children, layout = true, requiredPermission = null, adminOnly = false }) => {
  const {
    isAuthenticated,
    loading,
    hasPermission,
    isAdmin,
    isOperatorDashboardOnly,
    visibleEquipments,
    user,
  } = useAuth();
  const location = useLocation();

  // DEV MODE: Skip authentication
  if (DEV_MODE_NO_AUTH) {
    return layout ? <Layout>{children}</Layout> : children;
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Troca de senha obrigatoria pendente: nenhuma rota protegida abre antes.
  const passwordRedirect = passwordChangeRedirect(user, location.pathname);
  if (passwordRedirect) {
    return <Navigate to={passwordRedirect} replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (
    isOperatorDashboardOnly
    && !location.pathname.includes("/operator-dashboard")
    && location.pathname !== "/"
  ) {
    return <Navigate to={getOperatorDashboardPath(visibleEquipments)} replace />;
  }

  return layout ? <Layout>{children}</Layout> : children;
};

// Public Route wrapper (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  // DEV MODE: Redirect to home
  if (DEV_MODE_NO_AUTH) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    if (user?.must_change_password) {
      return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
    }
    return <Navigate to={sanitizeHome(user?.home_page) || "/"} replace />;
  }

  return children;
};

// Troca de senha: exige login, mas nao passa pelo guard de flag (evita loop).
const ChangePasswordRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  if (DEV_MODE_NO_AUTH) return <Navigate to="/" replace />;
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <ChangePasswordPage />;
};

const RootRoute = () => {
  const { isOperatorDashboardOnly, visibleEquipments } = useAuth();

  if (isOperatorDashboardOnly && visibleEquipments.length === 1) {
    return <Navigate to={getOperatorDashboardPath(visibleEquipments)} replace />;
  }

  return (
    <Layout>
      <DashboardPage />
    </Layout>
  );
};

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignUpPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path={CHANGE_PASSWORD_PATH}
        element={<ChangePasswordRoute />}
      />
      {/* Página pública de provisionamento IoT */}
      <Route
        path="/iotsignup"
        element={<IoTSignupPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute layout={false}>
            <RootRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipments"
        element={
          <ProtectedRoute>
            <EquipmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipment-types"
        element={
          <ProtectedRoute>
            <EquipmentTypesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipment-groups"
        element={
          <ProtectedRoute>
            <EquipmentGroupsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      {/* FASE D1: Rotas antigas /history e /live removidas - agora são redirects abaixo */}
      <Route
        path="/device-profiles"
        element={<Navigate to="/equipments" replace />}
      />
      <Route
        path="/access-control"
        element={
          <ProtectedRoute>
            <AccessControlPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-helper"
        element={
          <ProtectedRoute>
            <AIHelperPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/geofences"
        element={
          <ProtectedRoute>
            <GeofencesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/geofence-alerts"
        element={<Navigate to="/equipments" replace />}
      />
      <Route
        path="/routes"
        element={
          <ProtectedRoute>
            <RoutesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alertas"
        element={<Navigate to="/alerts" replace />}
      />
      <Route
        path="/operations/fuel-audit"
        element={
          <ProtectedRoute>
            <FuelAuditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/fuel-control"
        element={
          <ProtectedRoute>
            <FuelControlPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/activity"
        element={
          <ProtectedRoute>
            <OperationsActivityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/trips"
        element={
          <ProtectedRoute requiredPermission="trip:read">
            <TripsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/trips/:tripId"
        element={
          <ProtectedRoute requiredPermission="trip:read">
            <TripDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/trips/:tripId/print"
        element={
          <ProtectedRoute layout={false} requiredPermission="trip:read">
            <TripReportPrint />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations/reports"
        element={
          <ProtectedRoute requiredPermission="trip:read">
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/display-library"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/equipments/:equipmentId/mapping"
        element={<Navigate to="../sources" replace />}
      />
      <Route
        path="/equipments/:equipmentId/sources"
        element={
          <ProtectedRoute>
            <SourceRegistryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/widget-library"
        element={
          <ProtectedRoute adminOnly>
            <WidgetLibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/engine-models"
        element={
          <ProtectedRoute>
            <EngineModelsPage />
          </ProtectedRoute>
        }
      />
      {/* ITEM 6: Nova rota principal de monitoramento */}
      <Route
        path="/equipments/:equipmentId/monitor"
        element={
          <ProtectedRoute>
            <EquipmentMonitorPage />
          </ProtectedRoute>
        }
      />
      {/* Fase 1000: Central única de alertas por equipamento */}
      <Route
        path="/equipments/:equipmentId/alerts"
        element={
          <ProtectedRoute>
            <EquipmentAlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipments/:equipmentId/monitor/print"
        element={
          <ProtectedRoute layout={false}>
            <EquipmentMonitorPrint />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipments/:equipmentId/operator-dashboard"
        element={
          <ProtectedRoute layout={false} requiredPermission="operator_dashboard:read">
            <OperatorDashboardPage />
          </ProtectedRoute>
        }
      />
      {/* ITEM 6: Redirects das rotas antigas */}
      <Route
        path="/equipments/:equipmentId/dashboard"
        element={<Navigate to="../monitor" replace />}
      />
      <Route
        path="/equipments/:equipmentId/dashboard/:dashboardId"
        element={<Navigate to="../../monitor" replace />}
      />
      <Route
        path="/equipments/:equipmentId/history"
        element={<Navigate to="../monitor?tab=historico" replace />}
      />
      <Route
        path="/equipments/:equipmentId/live"
        element={<Navigate to="../monitor" replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppToaster() {
  const isMobile = useIsMobile();

  return (
    <Toaster
      position={isMobile ? "top-center" : "bottom-right"}
      richColors
      visibleToasts={isMobile ? 2 : 4}
      offset={isMobile ? 12 : 32}
      toastOptions={{
        classNames: {
          toast: "max-w-[calc(100vw-1.5rem)] text-sm",
        },
      }}
    />
  );
}

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <UnitsProvider>
            <TimezoneProvider>
              <AppRoutes />
              <AppToaster />
            </TimezoneProvider>
          </UnitsProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
